import { environment } from 'src/environments/environment';
import { InjectionToken, Signal } from '@angular/core';
import type { Observable } from 'rxjs';
import type { Expense } from '@shared/definitions/expense';

/**
 * Pattern B contract + DI token for Expenses — lifted to shared/definitions so
 * lower layers (core/shared) can inject the store without importing the feature.
 * The concrete impls + `provideExpensesStore()` stay in pages/apps/expenses/store;
 * the provider must be wired at the app root (app.config) since this token
 * intentionally carries NO providedIn:'root' factory.
 *
 * Workspace-scoped. Supports filtering by property.
 */
export interface IExpensesStore {
  readonly expenses: Signal<readonly Expense[]>;
  readonly loading: Signal<boolean>;
  readonly lastError: Signal<Error | null>;

  readonly totalCount: Signal<number>;
  readonly paidCount: Signal<number>;
  readonly unpaidCount: Signal<number>;
  readonly totalAmount: Signal<number>;

  refresh(force?: boolean): Promise<void>;

  getExpensesForProperty(propertyUID: string): readonly Expense[];
  getExpensesForBuilding(buildingUID: string): readonly Expense[];
  expensesForProperty$(propertyUID: string): Observable<readonly Expense[]>;
  expensesForBuilding$(buildingUID: string): Observable<readonly Expense[]>;
  getExpenseById(expenseUID: string): Expense | null;
  getExpenseByIdOnce(expenseUID: string): Promise<Expense | null>;

  addExpense(expense: Expense): Promise<string>;
  updateExpense(expense: Expense): Promise<void>;
  deleteExpense(expenseUID: string): Promise<void>;
}

export const EXPENSES_STORE = new InjectionToken<IExpensesStore>('EXPENSES_STORE');

export type ExpensesStoreMode = 'mock' | 'firestore';

export function resolveExpensesStoreMode(): ExpensesStoreMode {
  if (typeof window === 'undefined') return 'firestore';
  if (environment.production) return 'firestore'; // QA-only mock store: never honour ?store=mock in production (silent data loss).
  const url = new URL(window.location.href);
  const param = url.searchParams.get('store');
  if (param === 'firestore' || param === 'mock') {
    try {
      window.localStorage.setItem('expenses.storeMode', param);
    } catch {
      /* ignore */
    }
    return param;
  }
  try {
    const stored = window.localStorage.getItem('expenses.storeMode');
    if (stored === 'firestore' || stored === 'mock') return stored;
  } catch {
    /* ignore */
  }
  return 'firestore';
}
